'use client';

import React, { useRef, useEffect, useState } from 'react';
import { AlertTriangle, Zap, Eye, Radio, Move, Activity, Filter } from 'lucide-react';
import { useSession, ArtifactEvent } from './SessionProvider';
import Icon from '@/components/ui/AppIcon';


const TYPE_CONFIG: Record<
  ArtifactEvent['type'],
  { label: string; icon: React.ElementType; color: string; bg: string; border: string }
> = {
  'muscle': { label: 'Muscle', icon: Activity, color: 'text-orange-400', bg: 'bg-orange-950/30', border: 'border-orange-800/30' },
  'eye-blink': { label: 'Eye Blink', icon: Eye, color: 'text-sky-400', bg: 'bg-sky-950/30', border: 'border-sky-800/30' },
  'electrode-pop': { label: 'Elec. Pop', icon: Zap, color: 'text-red-400', bg: 'bg-red-950/30', border: 'border-red-800/30' },
  'movement': { label: 'Movement', icon: Move, color: 'text-amber-400', bg: 'bg-amber-950/30', border: 'border-amber-800/30' },
  'powerline': { label: '50 Hz Noise', icon: Radio, color: 'text-violet-400', bg: 'bg-violet-950/30', border: 'border-violet-800/30' },
};

const SEVERITY_BADGE: Record<ArtifactEvent['severity'], string> = {
  low: 'bg-zinc-800/60 text-zinc-400 border-zinc-700/50',
  medium: 'bg-amber-950/50 text-amber-400 border-amber-800/40',
  high: 'bg-red-950/50 text-red-400 border-red-800/40',
};

function formatEventTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '--:--:--';
  }
}

type FilterType = 'all' | ArtifactEvent['type'];

export default function ArtifactEventFeed() {
  const { artifactEvents } = useSession();
  const feedRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    if (autoScroll && feedRef.current) {
      feedRef.current.scrollTop = 0;
    }
  }, [artifactEvents, autoScroll]);

  const filtered =
    filter === 'all' ? artifactEvents : artifactEvents.filter((e) => e.type === filter);

  const highCount = artifactEvents.filter((e) => e.severity === 'high').length;

  return (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/40 p-4 flex flex-col h-full">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center border ${highCount > 0 ? 'bg-red-950/40 border-red-800/50' : 'bg-zinc-900 border-zinc-800'}`}>
            <AlertTriangle size={14} className={highCount > 0 ? 'text-red-400' : 'text-zinc-500'} />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-zinc-200">Artifact Events</p>
            <p className="text-[11px] text-zinc-600">
              {artifactEvents.length} total ·{' '}
              {highCount > 0 ? (
                <span className="text-red-400 font-medium">{highCount} high severity</span>
              ) : (
                <span className="text-emerald-400">No high severity</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setAutoScroll((v) => !v)}
            className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
              autoScroll
                ? 'bg-emerald-950/40 border-emerald-800/50 text-emerald-400' :'bg-zinc-900/60 border-zinc-800/50 text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {autoScroll ? '↑ Live' : 'Paused'}
          </button>
          <div className="relative">
            <button
              onClick={() => setShowFilter((v) => !v)}
              className="flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-800 bg-zinc-900/60 text-zinc-500 hover:text-zinc-300 text-[10px] transition-colors"
            >
              <Filter size={10} />
              {filter === 'all' ? 'All' : TYPE_CONFIG[filter as ArtifactEvent['type']].label}
            </button>
            {showFilter && (
              <div className="absolute right-0 top-full mt-1 w-36 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-50 overflow-hidden animate-scale-in">
                {(['all', 'muscle', 'eye-blink', 'electrode-pop', 'movement', 'powerline'] as FilterType[]).map(
                  (f) => (
                    <button
                      key={`filter-${f}`}
                      onClick={() => { setFilter(f); setShowFilter(false); }}
                      className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-zinc-800 transition-colors ${
                        filter === f ? 'text-emerald-400 bg-zinc-800/50' : 'text-zinc-400'
                      }`}
                    >
                      {f === 'all' ? 'All types' : TYPE_CONFIG[f as ArtifactEvent['type']].label}
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event feed */}
      <div
        ref={feedRef}
        className="flex-1 overflow-y-auto scrollbar-thin space-y-1.5 min-h-[200px] max-h-[320px]"
      >
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 text-center">
            <Activity size={20} className="text-zinc-700 mb-2" />
            <p className="text-[12px] text-zinc-600">No artifact events detected</p>
            <p className="text-[11px] text-zinc-700 mt-0.5">Signal is clean</p>
          </div>
        ) : (
          filtered.map((evt) => {
            const cfg = TYPE_CONFIG[evt.type];
            const Icon = cfg.icon;
            return (
              <div
                key={evt.id}
                className={`flex items-start gap-2.5 px-2.5 py-2 rounded-lg border transition-all duration-200 ${cfg.bg} ${cfg.border}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <Icon size={12} className={cfg.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] font-semibold ${cfg.color}`}>{cfg.label}</span>
                    <span className="text-[10px] font-mono text-zinc-500 bg-zinc-900/60 px-1.5 py-0.5 rounded">
                      {evt.channel}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wide ${SEVERITY_BADGE[evt.severity]}`}
                    >
                      {evt.severity}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-mono text-zinc-600">{formatEventTime(evt.timestamp)}</span>
                    <span className="text-[10px] text-zinc-700">·</span>
                    <span className="text-[10px] font-mono text-zinc-600">{evt.amplitude} µV</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Summary footer */}
      {artifactEvents.length > 0 && (
        <div className="flex-shrink-0 mt-3 pt-3 border-t border-zinc-800/60 grid grid-cols-3 gap-2">
          {(['muscle', 'eye-blink', 'movement'] as ArtifactEvent['type'][]).map((type) => {
            const count = artifactEvents.filter((e) => e.type === type).length;
            const cfg = TYPE_CONFIG[type];
            return (
              <div key={`summary-${type}`} className="text-center">
                <p className={`text-[13px] font-bold font-mono tabular-nums ${cfg.color}`}>{count}</p>
                <p className="text-[10px] text-zinc-600">{cfg.label}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}